import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import "./ReportGiacenzaMagazzino.css";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const GIORNI_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const FASCIA_LABEL = { mattina: "Mattina", pomeriggio: "Pomeriggio", sera: "Sera" };
const FASCIA_ORARIO = { mattina: "06–12", pomeriggio: "12–16", sera: "16–20" };
// useGrouping:'always' forza il punto delle migliaia: il locale it-IT ha minimumGroupingDigits=2,
// quindi senza di esso i numeri a 4 cifre (es. 2896) uscirebbero senza separatore.
const fmtInt = (n) => Number(n).toLocaleString("it-IT", { useGrouping: "always" });
const BLU = "#3B82F6";      // barra giacenza complessiva
const GIALLO = "#F5C518";   // etichetta linea capienza

const fmtKg = (n) => (n == null ? "—" : `${Number(n).toLocaleString("it-IT", { useGrouping: "always" })} kg`);

const ddmm = (dateStr) => {
    const [, m, d] = dateStr.split("-");
    return `${d}/${m}`;
};

const shiftDays = (dateStr, n) => {
    const dt = new Date(dateStr + "T00:00:00Z");
    dt.setUTCDate(dt.getUTCDate() + n);
    return dt.toISOString().slice(0, 10);
};

const ReportGiacenzaMagazzino = () => {
    const navigate = useNavigate();
    const today = new Date().toISOString().split("T")[0];

    const [anchorDate, setAnchorDate] = useState(today);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [isFullscreenChart, setIsFullscreenChart] = useState(false);
    const [collapsedDays, setCollapsedDays] = useState(new Set());
    // Modale dettaglio custom_id (click su una cella Entrate/Uscite della vista Flussi)
    const [flussoModal, setFlussoModal] = useState(null); // { title, items: [{id, kg}] }
    const [copied, setCopied] = useState(false);

    const toggleDay = (date) =>
        setCollapsedDays((prev) => {
            const next = new Set(prev);
            next.has(date) ? next.delete(date) : next.add(date);
            return next;
        });

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape" && isFullscreenChart) setIsFullscreenChart(false);
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [isFullscreenChart]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // giorni=14: la risposta copre la settimana del filtro + la successiva.
                // La tabella usa solo i primi 7 giorni; il grafico tutti e 14.
                const res = await fetch(
                    `${API_URL}/report-giacenza-magazzino/settimana?date=${anchorDate}&giorni=14`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
                );
                const result = await res.json();
                setData(result);
            } catch (err) {
                console.error("Errore caricamento giacenza magazzino:", err);
                setData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [anchorDate]);


    // Grafico vista "Flussi": una barra blu per giorno = giacenza complessiva (fine giornata + ceste
    // da svuotare su oggi e nei giorni futuri). Mostra 2 settimane (settimana del filtro + successiva).
    // Meno sfuocata per oggi/passato (consolidato), più sfuocata per il futuro (stima). Linea = capienza.
    const getFlussiChartOption = () => {
        if (!data || !data.days) return {};
        const days = data.days; // fino a 14 giorni (settimana filtro + successiva)
        const CAP = data.capacity;
        const labels = days.map((d, i) => `${GIORNI_SHORT[i % 7]} ${ddmm(d.date)}`);

        // Giacenza complessiva per giorno = come la colonna in tabella: giacenza fine giornata
        // (reale per il passato, stima per oggi/futuro) + ceste da svuotare (su oggi e sui futuri).
        const complessivaVals = days.map((d) => {
            const lastF = d.fasce[d.fasce.length - 1];
            const giac = lastF ? lastF.giacenzaFine : null;
            return giac == null ? 0 : giac + ((d.isToday || d.isFuture) ? (data.giacenzaDaSvuotareKg ?? 0) : 0);
        });

        // Barra blu: meno sfuocata per oggi+passato (dati consolidati), più sfuocata per il futuro (stima).
        const STYLE_PASSATO = { color: BLU, opacity: 0.75, borderRadius: [8, 8, 0, 0], shadowBlur: 8, shadowColor: "rgba(59,130,246,0.4)" };
        const STYLE_FUTURO = { color: BLU, opacity: 0.3, borderRadius: [8, 8, 0, 0], shadowBlur: 26, shadowColor: "rgba(59,130,246,0.55)" };
        const complessivaData = days.map((d, i) => ({
            value: complessivaVals[i],
            itemStyle: d.isFuture ? STYLE_FUTURO : STYLE_PASSATO,
        }));

        const maxData = Math.max(CAP, ...complessivaVals);
        // Etichette sopra la barra: valore in "K" (es. 32,5K) e, sotto, % di occupazione sulla capienza
        // (es. 44,6%). Decimale con la virgola (it-IT).
        const kLabel = (v) => `${(v / 1000).toFixed(1).replace(".", ",")}K`;
        const pctLabel = (v) => `${((v / CAP) * 100).toFixed(1).replace(".", ",")}%`;

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
                formatter: (params) => {
                    const i = params[0].dataIndex;
                    const d = days[i];
                    const stima = d.isFuture || d.isToday;
                    return `<strong>${GIORNI_SHORT[i % 7]} ${ddmm(d.date)}</strong><br/>Giacenza complessiva: <strong>${fmtKg(complessivaVals[i])}</strong> (${pctLabel(complessivaVals[i])})${stima ? " <em>(stima)</em>" : ""}`;
                },
            },
            grid: { left: 64, right: 24, bottom: 40, top: 40 },
            xAxis: { type: "category", data: labels, axisLabel: { fontSize: 12, color: "#475569" } },
            yAxis: {
                type: "value",
                max: Math.ceil((maxData * 1.18) / 1000) * 1000,
                axisLabel: {
                    formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val),
                    color: "#475569",
                },
                splitLine: { lineStyle: { color: "#EEF2F6" } },
            },
            series: [
                {
                    name: "Giacenza complessiva",
                    type: "bar",
                    barMaxWidth: 44,
                    data: complessivaData,
                    label: {
                        show: true,
                        position: "top",
                        formatter: (p) => `${kLabel(p.value)}\n${pctLabel(p.value)}`,
                        fontSize: 10,
                        lineHeight: 13,
                        fontWeight: 600,
                        color: "#334155",
                    },
                    markLine: {
                        silent: true,
                        symbol: "none",
                        lineStyle: { color: "#1E293B", width: 2, type: "dashed" },
                        label: {
                            formatter: `Capienza ${Number(CAP).toLocaleString("it-IT", { useGrouping: "always" })}`,
                            position: "insideEndTop",
                            color: "#1E293B",
                            backgroundColor: GIALLO,
                            padding: [3, 6],
                            borderRadius: 4,
                            fontWeight: "bold",
                            fontSize: 11,
                        },
                        data: [{ yAxis: CAP }],
                    },
                },
            ],
        };
    };

    // --- KPI pagina Entrate / Uscite ---
    // --- KPI della GIORNATA CORRENTE (oggi) — allineati ESATTAMENTE alla riga "oggi" della tabella ---
    // La giacenza del giorno è quella di FINE giornata (ultima fascia): per oggi è una STIMA (reale
    // corrente + ingressi/uscite ancora attesi), non il reale puntuale "adesso". Usare qui lo stesso
    // valore della tabella evita che card e griglia divergano.
    const oggiDay = data ? data.days.find((d) => d.isToday) : null;
    const lastFoggi = oggiDay && oggiDay.fasce.length ? oggiDay.fasce[oggiDay.fasce.length - 1] : null;
    const giacOggi = lastFoggi ? lastFoggi.giacenzaFine : null;
    // Entrate/Uscite oggi = reale puro (anche 0): niente proiezione teorica, come le colonne in tabella
    const entrateOggi = oggiDay ? oggiDay.fasce.reduce((a, f) => a + (f.entrateRealiKg ?? 0), 0) : null;
    const usciteOggi = oggiDay ? oggiDay.fasce.reduce((a, f) => a + (f.usciteRealiKg ?? 0), 0) : null;
    const cesteNrOggi = data ? (data.giacenzaDaSvuotareCeste ?? 0) : 0;
    const cesteKgOggi = data ? (data.giacenzaDaSvuotareKg ?? 0) : null;
    const balleNrOggi = data ? (data.balleNr ?? 0) : 0;
    const balleKgOggi = data ? (data.balleKg ?? 0) : null;
    // Complessiva (oggi) = giacenza fine giornata + ceste da svuotare (come colonna in tabella)
    const giacenzaComplessiva = giacOggi != null ? giacOggi + (cesteKgOggi ?? 0) : null;
    // Materiale a terra = complessiva − ceste da svuotare − balle (clamp a 0)
    const materialeATerraOggi =
        giacenzaComplessiva != null ? Math.max(0, giacenzaComplessiva - (cesteKgOggi ?? 0) - (balleKgOggi ?? 0)) : null;
    const percOccupazione =
        giacenzaComplessiva != null && data ? (giacenzaComplessiva / data.capacity) * 100 : null;

    const sumF = (d, k) => d.fasce.reduce((a, f) => a + (f[k] ?? 0), 0);

    // Valore Entrate/Uscite mostrato per giornata: reale per oggi/passato, teorico per i giorni
    // futuri (coerente con giornoCell, realOggi=true). Usato per i totali di colonna in fondo.
    const dayEntrateVal = (d) => (d.isFuture ? sumF(d, "entrateTeor") : sumF(d, "entrateRealiKg"));
    const dayUsciteVal = (d) => (d.isFuture ? sumF(d, "usciteTeor") : sumF(d, "usciteRealiKg"));

    // Apre il modale con la lista dei custom_id che compongono una cella Entrate/Uscite
    const openFlussoModal = (title, items) => {
        setCopied(false);
        setFlussoModal({ title, items: items || [] });
    };
    // Copia negli appunti la lista (una riga per custom_id: "id<TAB>kg")
    const copyItems = () => {
        if (!flussoModal) return;
        const txt = flussoModal.items.map((i) => `${i.cliente || i.id}\t${i.kg}`).join("\n");
        navigator.clipboard.writeText(txt).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    // Render reale/teorico + wrapper cliccabile (apre il modale custom_id)
    // Nelle giornate con dato reale (oggi/passato) il previsionale (teor, da turni/svuotamenti
    // programmati) è SEMPRE mostrato tra parentesi se > 0, anche quando l'actual è ancora 0:
    // così le fasce sommano al totale e si vede quanto è previsto vs raccolto finora. L'actual
    // compare di fianco man mano che arriva ("–" finché è 0).
    const renderReale = (seg, real, teor) => {
        const hasReal = real != null && real !== 0;
        const hasTeor = teor > 0;
        if (!hasReal && !hasTeor) return "–";
        return (
            <>
                {hasReal ? `${seg}${fmtInt(real)}` : "–"}
                {hasTeor && <span className="rgm-teorico"> (prev. {fmtInt(teor)})</span>}
            </>
        );
    };
    // Valori futuri/stimati: invece di "(Teorico)" si mostra il numero di eventi programmati
    // (entrate = turni, uscite = scarichi), con singolare/plurale.
    const plur = (n, sing, plur) => `${fmtInt(n)} ${n === 1 ? sing : plur}`;
    const renderTeor = (seg, teor, count, sing, plural) => (
        <>{teor > 0 ? `${seg}${fmtInt(teor)}` : "0"} <span className="rgm-teorico">({plur(count || 0, sing, plural)})</span></>
    );
    const clickable = (content, items, title) => {
        const hasItems = Array.isArray(items) && items.length > 0;
        if (!hasItems) return content;
        return (
            <button
                type="button"
                className="rgm-cell-link"
                onClick={(e) => { e.stopPropagation(); openFlussoModal(title, items); }}
                title="Vedi dettaglio custom_id"
            >
                {content}
            </button>
        );
    };
    // Cella FASCIA — futuro = teorico; oggi = reale se la fascia ha censite (>0), altrimenti teorico;
    // passato = reale. realOggi=true (uscite): oggi mostra SEMPRE il reale (anche 0), mai il teorico
    // (il teorico scarichi vale solo per il futuro).
    const fasciaCell = (d, real, teor, seg, items, title, count, sing, plural, realOggi = false) => {
        if (d.isFuture) return renderTeor(seg, teor, count, sing, plural);
        if (d.isToday && !(real > 0) && !realOggi) return renderTeor(seg, teor, count, sing, plural);
        return clickable(renderReale(seg, real, teor), items, title);
    };
    // Cella GIORNATA (rollup) — oggi = somma mista per fascia (reale dove censito, teorico altrove),
    // marcata con "*"; futuro = teorico; passato = reale. realOggi=true (uscite): oggi = somma reale
    // (anche 0), nessun teorico.
    const giornoCell = (d, realKey, teorKey, seg, items, title, countKey, sing, plural, realOggi = false) => {
        if (d.isFuture) return renderTeor(seg, sumF(d, teorKey), sumF(d, countKey), sing, plural);
        if (d.isToday) {
            if (realOggi) return clickable(renderReale(seg, sumF(d, realKey), sumF(d, teorKey)), items, title);
            const val = d.fasce.reduce((a, f) => a + ((f[realKey] > 0) ? f[realKey] : (f[teorKey] || 0)), 0);
            const content = val > 0 ? `${seg}${fmtInt(val)} *` : "–";
            return clickable(content, items, title);
        }
        return clickable(renderReale(seg, sumF(d, realKey), sumF(d, teorKey)), items, title);
    };

    // Vista "Flussi": grafico giacenza+uscite e tabella entrate/uscite (righe invariate)
    const renderFlussi = () => (
        <>
            <div className={`rgm-chart-card${isFullscreenChart ? " rgm-fullscreen" : ""}`}>
                <RotateDevice active={isFullscreenChart} />
                <div className="rgm-chart-header">
                    <div className="rgm-legend">
                        <span className="rgm-legend-item"><i className="rgm-sw" style={{ background: BLU }} /> Giacenza complessiva</span>
                    </div>
                    <button
                        className="rgm-fullscreen-btn"
                        onClick={() => setIsFullscreenChart(!isFullscreenChart)}
                        title={isFullscreenChart ? "Esci da tutto schermo" : "Tutto schermo"}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {isFullscreenChart ? (
                                <>
                                    <polyline points="4 14 10 14 10 20"></polyline>
                                    <polyline points="20 10 14 10 14 4"></polyline>
                                </>
                            ) : (
                                <>
                                    <polyline points="15 3 21 3 21 9"></polyline>
                                    <polyline points="9 21 3 21 3 15"></polyline>
                                </>
                            )}
                        </svg>
                    </button>
                </div>
                {data && data.days && data.days.length ? (
                    <ReactECharts
                        option={getFlussiChartOption()}
                        notMerge={true}
                        style={{ height: 288 }}
                        opts={{ renderer: "canvas" }}
                    />
                ) : (
                    <div className="rgm-empty">Nessun dato disponibile per questa settimana.</div>
                )}
            </div>

            {data && data.days && (
                <div className="rgm-table-card">
                    <div className="rgm-table-head">
                        <h3 className="rgm-table-title">Flusso entrate / uscite per fascia oraria</h3>
                        {(() => {
                            const allDates = data.days.slice(0, 7).map((d) => d.date);
                            const allCollapsed = allDates.every((dt) => collapsedDays.has(dt));
                            return (
                                <button
                                    className="rgm-drill-btn"
                                    onClick={() => setCollapsedDays(allCollapsed ? new Set() : new Set(allDates))}
                                    title={allCollapsed ? "Mostra i turni di ogni giornata" : "Mostra solo le giornate"}
                                >
                                    {allCollapsed ? "▾ Espandi turni" : "▸ Comprimi turni"}
                                </button>
                            );
                        })()}
                    </div>
                    <div className="rgm-table-wrapper">
                        <table className="rgm-table">
                            <thead>
                                <tr>
                                    <th className="rgm-th-left">Giorno / Fascia</th>
                                    <th>Entrate</th>
                                    <th>Uscite</th>
                                    <th>Giacenza complessiva</th>
                                    <th>Ceste da svuotare</th>
                                    <th>Balle</th>
                                    <th>Materiale a terra</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.days.slice(0, 7).map((d, di) => {
                                    const expanded = !collapsedDays.has(d.date);
                                    const lastF = d.fasce[d.fasce.length - 1];
                                    const giac = lastF ? lastF.giacenzaFine : null;
                                    const giacStim = !!(lastF && lastF.giacenzaStimata);
                                    // Ceste da svuotare (temporaneo): valore "attuale" unico. La complessiva le
                                    // include su OGGI e sui giorni FUTURI (così il giorno dopo parte dal totale
                                    // complessivo del giorno precedente); nei giorni passati resta = sola giacenza.
                                    const temp = data.giacenzaDaSvuotareKg ?? 0;
                                    const cesteTemp = data.giacenzaDaSvuotareCeste ?? 0;
                                    const balleNr = data.balleNr ?? 0;
                                    const balleKg = data.balleKg ?? 0;
                                    const complessiva = giac != null ? giac + ((d.isToday || d.isFuture) ? temp : 0) : null;
                                    // Materiale a terra = complessiva − ceste da svuotare − balle. Come ceste/balle è
                                    // un valore "attuale": mostrato solo su oggi (altrove "–"). Clamp a 0: non può essere negativo.
                                    const matTerra = d.isToday && complessiva != null ? Math.max(0, complessiva - temp - balleKg) : null;
                                    const ddmmLabel = `${GIORNI_SHORT[di]} ${ddmm(d.date)}`;
                                    const entrateItems = d.fasce.flatMap((f) => f.entrateItems || []);
                                    const usciteItems = d.fasce.flatMap((f) => f.usciteItems || []);
                                    return (
                                        <Fragment key={d.date}>
                                            <tr className={`rgm-day-row${d.isToday ? " rgm-today-row" : ""}`} onClick={() => toggleDay(d.date)}>
                                                <td className="rgm-th-left">
                                                    <span className={`rgm-caret${expanded ? " open" : ""}`}>▸</span>
                                                    <strong>{ddmmLabel}</strong>
                                                    {d.isToday && <span className="rgm-badge oggi">oggi</span>}
                                                    {d.isFuture && <span className="rgm-badge fut">previsto</span>}
                                                </td>
                                                <td>{giornoCell(d, "entrateRealiKg", "entrateTeor", "", entrateItems, `Entrate · ${ddmmLabel}`, "veicoliTurno", "turno", "turni", true)}</td>
                                                <td>{giornoCell(d, "usciteRealiKg", "usciteTeor", "−", usciteItems, `Uscite · ${ddmmLabel}`, "svuotTeorici", "scarico", "scarichi", true)}</td>
                                                <td className={complessiva != null && complessiva > data.capacity ? "rgm-over" : ""}>{complessiva == null ? "–" : `${fmtInt(complessiva)}${giacStim ? " *" : ""}`}</td>
                                                <td>
                                                    {d.isToday
                                                        ? (data.giacenzaDaSvuotareItems && data.giacenzaDaSvuotareItems.length
                                                            ? <button type="button" className="rgm-cell-link" onClick={(e) => { e.stopPropagation(); openFlussoModal("Ceste da svuotare · oggi", data.giacenzaDaSvuotareItems); }} title="Vedi ceste da svuotare">{fmtInt(cesteTemp)} ceste · {fmtInt(temp)} kg</button>
                                                            : `${fmtInt(cesteTemp)} ceste · ${fmtInt(temp)} kg`)
                                                        : "–"}
                                                </td>
                                                <td>
                                                    {d.isToday
                                                        ? (data.balleItems && data.balleItems.length
                                                            ? <button type="button" className="rgm-cell-link" onClick={(e) => { e.stopPropagation(); openFlussoModal("Balle · oggi", data.balleItems); }} title="Vedi balle">{fmtInt(balleNr)} balle · {fmtInt(balleKg)} kg</button>
                                                            : `${fmtInt(balleNr)} balle · ${fmtInt(balleKg)} kg`)
                                                        : "–"}
                                                </td>
                                                <td>{matTerra == null ? "–" : `${fmtInt(matTerra)}${giacStim ? " *" : ""}`}</td>
                                            </tr>
                                            {expanded && d.fasce.map((f) => (
                                                <tr className={`rgm-fascia-row${d.isToday ? " rgm-today-row" : ""}`} key={f.nome}>
                                                    <td className="rgm-th-left rgm-fascia-name">
                                                        {FASCIA_LABEL[f.nome]} <span className="rgm-fascia-ora">{FASCIA_ORARIO[f.nome]}</span>
                                                    </td>
                                                    <td>{fasciaCell(d, f.entrateRealiKg, f.entrateTeor, "", f.entrateItems, `Entrate · ${ddmmLabel} · ${FASCIA_LABEL[f.nome]}`, f.veicoliTurno, "turno", "turni", true)}</td>
                                                    <td>{fasciaCell(d, f.usciteRealiKg, f.usciteTeor, "−", f.usciteItems, `Uscite · ${ddmmLabel} · ${FASCIA_LABEL[f.nome]}`, f.svuotTeorici, "scarico", "scarichi", true)}</td>
                                                    <td>–</td>
                                                    <td>–</td>
                                                    <td>–</td>
                                                    <td>–</td>
                                                </tr>
                                            ))}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                            {(() => {
                                // Totali di colonna della settimana: solo Entrate/Uscite (le altre colonne
                                // sono valori "attuali"/stock, non ha senso sommarle → "–").
                                const week = data.days.slice(0, 7);
                                const totEntrate = week.reduce((a, d) => a + dayEntrateVal(d), 0);
                                const totUscite = week.reduce((a, d) => a + dayUsciteVal(d), 0);
                                return (
                                    <tfoot>
                                        <tr className="rgm-week-total">
                                            <td className="rgm-th-left">Totale settimana</td>
                                            <td>{totEntrate > 0 ? fmtInt(totEntrate) : "–"}</td>
                                            <td>{totUscite > 0 ? `−${fmtInt(totUscite)}` : "–"}</td>
                                            <td>–</td>
                                            <td>–</td>
                                            <td>–</td>
                                            <td>–</td>
                                        </tr>
                                    </tfoot>
                                );
                            })()}
                        </table>
                    </div>
                    <p className="rgm-table-note">
                        <strong>Entrate</strong> = ceste pesate (C29), imputate alla <strong>prima pesata</strong> (primo peso valorizzato, qualunque stato). <strong>Oggi e nei giorni passati</strong> si mostra il valore <strong>reale</strong> (anche 0) e, tra parentesi, il valore <strong>teorico previsto</strong> in kg <em>(prev. …)</em> per confrontare consuntivo e previsione. Nei giorni <strong>futuri</strong>, stima di default (1.500 kg/veicolo·turno) col numero di <em>turni programmati</em>. <strong>Uscite</strong> = spedizioni reali (C30, stato "Spedito"), imputate alla <strong>Data spedizione</strong> (non alla data in cui vengono marcate "Spedito"); nei giorni futuri, svuotamenti programmati C126 col numero di <em>scarichi programmati</em>. Clicca su una cella Entrate/Uscite per il dettaglio (entrate = custom_id cesta; uscite = cliente). <strong>Giacenza complessiva</strong> = giacenza di fine giornata + ceste da svuotare; mostrata solo a livello di giornata (i turni non la ripartiscono), e il giorno dopo parte da questo totale. <strong>Ceste da svuotare</strong> (temporaneo) = ceste attualmente pesate ma non ancora svuotate (numero ceste + kg, valore attuale mostrato su "oggi"); clicca per il dettaglio delle singole ceste. <strong>Balle</strong> = balle in stato "Nuovo" (C125), numero balle + somma peso netto (valore attuale mostrato su "oggi"); clicca per il dettaglio delle singole balle. <strong>Materiale a terra</strong> = complessiva − ceste da svuotare − balle (solo kg, mostrato su "oggi"); zero = niente a terra. I valori con <strong>*</strong> sono stimati (oggi/futuro). <strong>Totale settimana</strong> (in fondo) = somma di Entrate e Uscite sui 7 giorni (reale per oggi/passato, stima per i giorni futuri); le altre colonne non vengono totalizzate perché sono valori attuali di giacenza.
                    </p>
                </div>
            )}

            {flussoModal && (
                <div className="rgm-modal-overlay" onClick={() => setFlussoModal(null)}>
                    <div className="rgm-modal rgm-modal-flusso" onClick={(e) => e.stopPropagation()}>
                        <div className="rgm-modal-header">
                            <h3>{flussoModal.title} <span className="rgm-modal-count">({flussoModal.items.length})</span></h3>
                            <button className="rgm-modal-close" onClick={() => setFlussoModal(null)}>×</button>
                        </div>
                        <div className="rgm-modal-body">
                            {(() => {
                            // Uscite → colonna "cliente"; entrate/ceste → "custom_id" (gli item uscite hanno il campo cliente)
                            const colLabel = flussoModal.items.some((it) => it.cliente != null) ? "cliente" : "custom_id";
                            return flussoModal.items.length === 0 ? (
                                <div className="rgm-empty">Nessun dato per questo valore.</div>
                            ) : (
                                <table className="rgm-items-table">
                                    <thead>
                                        <tr><th>{colLabel}</th><th>kg</th></tr>
                                    </thead>
                                    <tbody>
                                        {flussoModal.items.map((it, idx) => (
                                            <tr key={`${it.cliente || it.id}-${idx}`}>
                                                <td>{it.cliente || it.id}</td>
                                                <td>{fmtInt(it.kg)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            );
                            })()}
                        </div>
                        <div className="rgm-modal-footer">
                            <button className="rgm-copy-btn" onClick={copyItems} disabled={!flussoModal.items.length}>
                                {copied ? "✓ Copiato" : "Copia"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div className="rgm-container">
            <header className="rgm-header">
                <button className="rgm-back" onClick={() => navigate("/automazione")}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Indietro
                </button>
                <div className="rgm-title-block">
                    <div className="rgm-title-row">
                        <h2>Report Giacenza Magazzino</h2>
                        <button className="rgm-info-btn" onClick={() => setShowInfoModal(true)} title="Informazioni">
                            <span>i</span>
                        </button>
                    </div>
                    <p className="rgm-subtitle">Entrate / Uscite magazzino abiti per settimana (ceste C29 · spedizioni C30)</p>
                </div>
            </header>

            {showInfoModal && (
                <div className="rgm-modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="rgm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="rgm-modal-header">
                            <h3>Come leggere il report</h3>
                            <button className="rgm-modal-close" onClick={() => setShowInfoModal(false)}>×</button>
                        </div>
                        <div className="rgm-modal-body">
                            <p>Settimana ISO (Lun–Dom). Flusso del magazzino abiti con la giacenza che ne deriva. Le <strong>card in alto</strong> riassumono i valori della <strong>giornata corrente</strong>.</p>
                            <p><strong>Grafico:</strong> mostra <strong>due settimane</strong> (quella selezionata + la successiva). Una <strong>barra blu</strong> per giorno = <strong>giacenza complessiva</strong> di fine giornata (+ ceste da svuotare su oggi e nei giorni futuri). Sopra ogni barra: il valore in K e la <strong>% di occupazione</strong> sulla capienza. È <strong>meno sfocata</strong> per oggi e i giorni passati (dato consolidato) e <strong>più sfocata</strong> per il futuro (stima). La linea tratteggiata è la <strong>capienza massima (72.800 kg)</strong>. La tabella sotto mostra invece solo la settimana selezionata.</p>
                            <p><strong>Colonne della tabella:</strong></p>
                            <ul>
                                <li><strong>Entrate</strong> = ceste pesate (C29), imputate alla prima pesata. Oggi e nei giorni passati si mostra il valore <strong>reale</strong> (anche 0) con tra parentesi il <strong>teorico previsto</strong> in kg <em>(prev. …)</em> per il confronto actual/previsto; solo nei giorni futuri la stima col numero di <em>turni programmati</em>.</li>
                                <li><strong>Uscite</strong> = spedizioni reali (C30, stato "Spedito"), imputate alla <strong>Data spedizione</strong> (campo del post), non alla data in cui vengono marcate "Spedito"; oggi e nei giorni passati con tra parentesi il <strong>teorico previsto</strong> in kg <em>(prev. …)</em>; nel futuro gli svuotamenti programmati (C126) col numero di <em>scarichi programmati</em>.</li>
                                <li><strong>Giacenza complessiva</strong> = giacenza di fine giornata + ceste da svuotare; mostrata solo a livello di giornata (i turni non la ripartiscono) e il giorno successivo parte da questo totale. <strong>*</strong> = stimata (oggi/futuro).</li>
                                <li><strong>Ceste da svuotare</strong> = ceste pesate ma non ancora svuotate (C29): numero + kg, valore attuale mostrato su "oggi".</li>
                                <li><strong>Balle</strong> = balle in stato "Nuovo" (C125): numero + somma del peso netto, valore attuale su "oggi".</li>
                                <li><strong>Materiale a terra</strong> = complessiva − ceste da svuotare − balle (solo kg, su "oggi"); zero = niente a terra.</li>
                            </ul>
                            <p>Clicca su una cella per il dettaglio: <strong>Entrate</strong>, <strong>Ceste da svuotare</strong> e <strong>Balle</strong> mostrano i <strong>custom_id</strong> con i kg; le <strong>Uscite</strong> mostrano il <strong>cliente</strong> con i kg.</p>
                            <p className="rgm-modal-note">Fasce orarie: Mattina (fino 12:00), Pomeriggio (fino 16:00), Sera.</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="rgm-content">
                {/* Controlli settimana */}
                <div className="rgm-week-bar">
                    <button className="rgm-week-nav" onClick={() => setAnchorDate(shiftDays(anchorDate, -7))} title="Settimana precedente">
                        &larr;
                    </button>
                    <div className="rgm-week-picker">
                        <DatePickerModal
                            value={anchorDate}
                            onChange={(e) => setAnchorDate(e.target.value)}
                            label="Seleziona un giorno della settimana"
                        />
                    </div>
                    <button className="rgm-week-nav" onClick={() => setAnchorDate(shiftDays(anchorDate, 7))} title="Settimana successiva">
                        &rarr;
                    </button>
                    <button className="rgm-week-today" onClick={() => setAnchorDate(today)}>Oggi</button>
                    {data && (
                        <span className="rgm-week-label">
                            Settimana dal <strong>{ddmm(data.weekStart)}</strong> al <strong>{ddmm(data.weekEnd)}</strong>
                        </span>
                    )}
                </div>

                {/* KPI della GIORNATA CORRENTE — coerenti con le colonne della tabella */}
                <div className="rgm-kpi-strip">
                    <div className="rgm-kpi">
                        <span className="rgm-kpi-value">{entrateOggi != null ? fmtInt(entrateOggi) : "—"}</span>
                        <span className="rgm-kpi-label">Entrate oggi (kg)</span>
                    </div>
                    <div className="rgm-kpi">
                        <span className="rgm-kpi-value">{usciteOggi != null ? fmtInt(usciteOggi) : "—"}</span>
                        <span className="rgm-kpi-label">Uscite oggi (kg)</span>
                    </div>
                    <div className="rgm-kpi">
                        <span className={`rgm-kpi-value ${giacenzaComplessiva != null && giacenzaComplessiva > (data ? data.capacity : Infinity) ? "rgm-over" : ""}`}>
                            {giacenzaComplessiva != null ? fmtInt(giacenzaComplessiva) : "—"}
                        </span>
                        <span className="rgm-kpi-label">Giacenza complessiva (kg)</span>
                    </div>
                    <div className="rgm-kpi">
                        <span className="rgm-kpi-value">{cesteKgOggi != null ? fmtInt(cesteKgOggi) : "—"}</span>
                        <span className="rgm-kpi-label">Ceste da svuotare · {fmtInt(cesteNrOggi)} (kg)</span>
                    </div>
                    <div className="rgm-kpi">
                        <span className="rgm-kpi-value">{balleKgOggi != null ? fmtInt(balleKgOggi) : "—"}</span>
                        <span className="rgm-kpi-label">Balle · {fmtInt(balleNrOggi)} (kg)</span>
                    </div>
                    <div className="rgm-kpi">
                        <span className="rgm-kpi-value">{materialeATerraOggi != null ? fmtInt(materialeATerraOggi) : "—"}</span>
                        <span className="rgm-kpi-label">Materiale a terra (kg)</span>
                    </div>
                    <div className="rgm-kpi">
                        <span className={`rgm-kpi-value ${percOccupazione != null && percOccupazione > 100 ? "rgm-over" : ""}`}>
                            {percOccupazione != null ? `${percOccupazione.toFixed(1).replace(".", ",")}%` : "—"}
                        </span>
                        <span className="rgm-kpi-label">Occupazione (complessiva)</span>
                    </div>
                </div>

                {loading ? (
                    <Lf23Loader />
                ) : (
                    renderFlussi()
                )}
            </div>
        </div>
    );
};

export default ReportGiacenzaMagazzino;
